import { useEffect, useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@actschurch/ccms-sdk';
import { Stack } from '@/components/ui/layout';
import { Title, Text, Button } from '@/components/ui/core';
import { PasswordInput } from '@/components/ui/inputs';
import { toast } from '@/components/ui/providers/AlertProvider';
import { LoadingScreen } from '@/components/common/LoadingScreen';

type Phase = 'exchanging' | 'linkRequired' | 'redirecting' | 'failed';

/**
 * Lands here after the user authorizes us in Planning Centre.
 * Reads `code`+`state` from the URL, calls the SDK callback,
 * and either signs the user in or prompts for password to confirm a link.
 */
export default function PlanningCentreCallbackPage() {
  const navigate = useNavigate();
  const { planningCentre } = useAuth();

  const [phase, setPhase] = useState<Phase>('exchanging');
  const [error, setError] = useState<string | null>(null);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [linkEmail, setLinkEmail] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmingLink, setConfirmingLink] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const errParam = params.get('error');

    if (errParam) {
      setError(`Planning Centre returned an error: ${errParam}`);
      setPhase('failed');
      return;
    }

    if (!code || !state) {
      setError('Missing code or state in callback URL');
      setPhase('failed');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const result = await planningCentre.callback(code, state);
        if (cancelled) return;

        if (result.status === 'LoggedIn') {
          setPhase('redirecting');
          // Clear URL params before redirect
          navigate('/', { replace: true });
        } else {
          setLinkToken(result.linkToken);
          setLinkEmail(result.email);
          setPhase('linkRequired');
        }
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Sign in failed');
        setPhase('failed');
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirmLink = async (e: FormEvent) => {
    e.preventDefault();
    if (!linkToken) return;
    setConfirmingLink(true);
    try {
      await planningCentre.confirmLink(linkToken, password);
      navigate('/', { replace: true });
    } catch (err) {
      toast.error('Could not link account', err instanceof Error ? err.message : 'Wrong password?');
    } finally {
      setConfirmingLink(false);
    }
  };

  if (phase === 'exchanging' || phase === 'redirecting') {
    return <LoadingScreen />;
  }

  if (phase === 'failed') {
    return (
      <Stack gap="md" align="center">
        <Title order={3}>Sign in failed</Title>
        <Text c="dimmed" ta="center">{error}</Text>
        <Button onClick={() => navigate('/auth/login', { replace: true })}>Back to sign in</Button>
      </Stack>
    );
  }

  // linkRequired
  return (
    <Stack gap="lg">
      <Stack align="center" gap="xs">
        <Title order={3} ta="center">Confirm your CCMS password</Title>
        <Text c="dimmed" size="sm" ta="center">
          We found an existing CCMS account for <strong>{linkEmail}</strong>. Enter your CCMS password to link
          your Planning Centre identity to it.
        </Text>
      </Stack>

      <form onSubmit={handleConfirmLink}>
        <Stack gap="md">
          <PasswordInput
            placeholder="Your CCMS password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            size="md"
            radius="md"
          />
          <Button
            type="submit"
            fullWidth
            size="md"
            radius="md"
            variant="gradient"
            gradient={{ from: 'indigo', to: 'violet' }}
            isBusy={confirmingLink}
            disabled={confirmingLink || !password}
          >
            {confirmingLink ? 'Linking...' : 'Link and sign in'}
          </Button>
          <Button
            type="button"
            fullWidth
            variant="subtle"
            onClick={() => navigate('/auth/login', { replace: true })}
          >
            Cancel
          </Button>
        </Stack>
      </form>
    </Stack>
  );
}
